import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Baby,
  CaretRight,
  Storefront,
  Lifebuoy,
  Waveform,
  Quotes,
  CloudLightning,
  CloudRain,
  Cloud,
  Sun,
  Heart,
} from "@phosphor-icons/react";
import { dailyMessageOfDay, MOODS, TESTIMONIALS, babyAgeLabel, ILLUSTRATIONS, AVATAR_DEFAULT } from "../data/staticData";
import { getJSON, K } from "../lib/storage";
import { trackEvent } from "../lib/events";
import { PREMA_BY_ID } from "../data/prematuriteData";

const PREMA_COUNT = Object.keys(PREMA_BY_ID).length;
const MOOD_ICONS = { CloudLightning, CloudRain, Cloud, Sun, Heart };

export default function Home() {
  const navigate = useNavigate();
  const [tIndex, setTIndex] = useState(0);
  const profile = getJSON(K.profile, {});
  const ageLabel = babyAgeLabel(profile?.baby_birth_date);
  const greetingName = profile?.name ? `, ${profile.name}` : "";
  const avatarSrc = profile?.avatar || AVATAR_DEFAULT;

  useEffect(() => {
    trackEvent("app_open");
  }, []);

  useEffect(() => {
    const id = setInterval(() => setTIndex((i) => (i + 1) % TESTIMONIALS.length), 6000);
    return () => clearInterval(id);
  }, []);

  const t = TESTIMONIALS[tIndex];

  return (
    <div>
      <header className="px-6 pt-8">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[13px] font-semibold uppercase tracking-[0.18em] text-[#36302B]/45">
              Renaître
            </p>
            <h1 className="font-display text-3xl font-medium text-[#36302B]">
              Bonjour{greetingName}
            </h1>
            {ageLabel && <p className="mt-1 text-[14px] text-[#C57B67]">{ageLabel}</p>}
          </div>
          <Link
            to="/profil"
            data-testid="home-avatar"
            className="h-12 w-12 overflow-hidden rounded-full border-2 border-white shadow-sm"
          >
            <img src={avatarSrc} alt="Profil" className="h-full w-full object-cover" />
          </Link>
        </div>
      </header>

      {/* Hero carousel — toutes les origines représentées */}
      <section className="mt-5 px-6">
        <HeroCarousel />
        <div className="mt-3 rounded-2xl bg-[#F2EDE4] px-5 py-4">
          <p className="font-display text-[16px] italic leading-snug text-[#36302B]/85">
            {dailyMessageOfDay()}
          </p>
        </div>
      </section>

      {/* Priority: Naissance prématurée — always first */}
      <section className="mt-6 px-6">
        <Link
          to="/prematurite"
          data-testid="home-prema-card"
          className="flex items-center gap-4 rounded-[26px] border border-[#D8A7A7]/40 bg-white p-5 shadow-sm transition-transform hover:-translate-y-1"
        >
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#9b8ec4]/15">
            <Baby size={30} color="#7E70AE" weight="duotone" />
          </div>
          <div className="flex-1">
            <p className="font-display text-[18px] font-medium text-[#36302B]">Naissance prématurée</p>
            <p className="mt-0.5 text-[13.5px] leading-snug text-[#36302B]/60">
              Un cocon dans le cocon — {PREMA_COUNT} fiches, 7 espaces pour vous accompagner.
            </p>
          </div>
          <CaretRight size={22} color="#36302B" weight="bold" className="opacity-30" />
        </Link>
      </section>

      {/* Mood shortcuts */}
      <section className="mt-7 px-6">
        <h2 className="font-display text-xl font-medium text-[#36302B]">
          Comment vous sentez-vous ?
        </h2>
        <div className="mt-3 flex justify-between gap-2">
          {MOODS.map((m) => {
            const Icon = MOOD_ICONS[m.icon];
            return (
              <button
                key={m.value}
                data-testid={`home-mood-${m.value}`}
                onClick={() => navigate(`/journal?mood=${m.value}`)}
                className="flex flex-1 flex-col items-center gap-1.5 rounded-3xl border border-[#36302B]/8 bg-white py-3 transition-colors hover:border-[#C57B67]"
              >
                <Icon size={26} color={m.color} weight="duotone" />
                <span className="text-[10.5px] font-semibold text-[#36302B]/60">{m.label}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Quick links */}
      <section className="mt-7 grid grid-cols-3 gap-3 px-6">
        <QuickLink to="/meditation" icon={Waveform} label="Méditation" color="#9CAEA9" testid="home-link-meditation" />
        <QuickLink to="/aide" icon={Lifebuoy} label="Aide" color="#C57B67" testid="home-link-aide" />
        <QuickLink to="/boutique" icon={Storefront} label="Boutique" color="#D3B683" testid="home-link-boutique" />
      </section>

      {/* Testimonials */}
      <section className="mt-8 px-6">
        <div className="rounded-[26px] bg-[#F2EDE4] p-6">
          <div className="mb-3 flex items-center justify-between">
            <Quotes size={26} color="#C57B67" weight="fill" />
            <span className="rounded-full bg-white px-3 py-1 text-[10.5px] font-bold uppercase tracking-wide text-[#36302B]/50">
              Témoignage illustratif
            </span>
          </div>
          <p key={tIndex} className="soft-fade font-display text-[17px] italic leading-relaxed text-[#36302B]/90">
            « {t.quote} »
          </p>
          <p className="mt-3 text-[13.5px] font-semibold text-[#C57B67]">{t.author}</p>
          <div className="mt-4 flex gap-1.5">
            {TESTIMONIALS.map((_, i) => (
              <span
                key={i}
                className="h-1.5 rounded-full transition-all"
                style={{
                  width: i === tIndex ? 20 : 6,
                  backgroundColor: i === tIndex ? "#C57B67" : "rgba(54,48,43,0.15)",
                }}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function QuickLink({ to, icon: Icon, label, color, testid }) {  return (
    <Link
      to={to}
      data-testid={testid}
      className="flex flex-col items-center gap-2 rounded-3xl border border-[#36302B]/6 bg-white py-4 transition-transform hover:-translate-y-1"
    >
      <div className="flex h-11 w-11 items-center justify-center rounded-2xl" style={{ backgroundColor: `${color}22` }}>
        <Icon size={24} color={color} weight="duotone" />
      </div>
      <span className="text-[12px] font-semibold text-[#36302B]/70">{label}</span>
    </Link>
  );
}


function HeroCarousel() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((p) => (p + 1) % ILLUSTRATIONS.length), 3500);
    return () => clearInterval(id);
  }, []);
  return (
    <div
      data-testid="home-hero-carousel"
      className="relative overflow-hidden rounded-[28px] shadow-[0_10px_40px_rgba(54,48,43,0.12)]"
    >
      <div
        className="flex transition-transform duration-700 ease-out"
        style={{ transform: `translateX(-${i * 100}%)` }}
      >
        {ILLUSTRATIONS.map((im, idx) => (
          <img
            key={idx}
            src={im.src}
            alt="Maman et bébé, illustration"
            data-testid={`hero-slide-${idx}`}
            className="aspect-[4/3] w-full shrink-0 object-cover object-center"
          />
        ))}
      </div>
      <span className="sr-only">Toutes les origines représentées</span>
      <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
        {ILLUSTRATIONS.map((_, idx) => (
          <button
            key={idx}
            data-testid={`hero-dot-${idx}`}
            onClick={() => setI(idx)}
            aria-label={`Illustration ${idx + 1}`}
            className="h-1.5 rounded-full transition-all"
            style={{ width: idx === i ? 18 : 6, backgroundColor: idx === i ? "#FFFFFF" : "rgba(255,255,255,0.55)" }}
          />
        ))}
      </div>
    </div>
  );
}
